import React, { createContext, useCallback, useContext } from 'react'
import { Pressable } from 'react-native'
import { Text } from 'react-native-elements'
import { Feather } from '../../components/icon/vectorIcons'
import { CellRendererProps, TableProps } from '../../components/common/Table'
import NoWrapText from '../../components/cell/NoWrapText'
import MultiValueCellContent from '../../components/cell/MultiValueCellContent'
import { formatDate } from '../../utils/format'
import TitleCell from '../../components/cell/TitleCellContent'
import theme from '../../constants/Theme'
import useLinkToScreen from '../../hooks/useLinkToScreen'
import useTranslation from '../../hooks/useTranslation'
import openDocletPreview from './builder/openDocletPreview'
import { DocletToDelete } from './DeleteDocletDialog'
import { ShareDocletGatingData } from './api'

interface DocletRow {
  id: string
  name: string | null
  displayName: string | null
  createdAt: string
  isPublished: boolean
  gating: { modality: ShareDocletGatingData['modality'] }
  communities: { id: string; name: string }[]
}

// Row actions the list screen handles (the columns are static, so they reach
// it through context)
export const DocletRowActionsContext = createContext<{
  onDelete: (doclet: DocletToDelete) => void
}>({ onDelete: () => {} })

// Sort keys are ShareDocletSortBy values. Cells take CellRendererProps<any>
// (as Library's do) because the shared Table isn't generic over its rows;
// rows have the DocletRow shape.
export const columns: TableProps<DocletRow>['columns'] = [
  {
    header: 'Created',
    width: 117,
    sort: {
      key: 'createdAt',
      defaultDir: 'desc',
      isDefault: true
    },
    Cell: ({ item }: CellRendererProps<any>) => (
      <NoWrapText>{formatDate(item.createdAt)}</NoWrapText>
    )
  },
  {
    header: 'Title',
    sort: {
      key: 'name'
    },
    filter: {
      key: 'query',
      defaultValue: '',
      type: 'search'
    },
    Cell: ({ item }: CellRendererProps<any>) => {
      const { t } = useTranslation()
      const linkToScreen = useLinkToScreen()
      const onPress = useCallback(() => {
        linkToScreen('DocletEdit', { id: item.id })
      }, [linkToScreen, item.id])
      return (
        <TitleCell
          item={{ title: item.displayName || t('doclets:untitled') }}
          onPress={onPress}
        />
      )
    }
  },
  {
    header: 'Communities',
    width: 220,
    Cell: ({ item }: CellRendererProps<any>) => (
      <MultiValueCellContent values={item.communities} />
    )
  },
  {
    header: 'Published',
    width: 110,
    Cell: ({ item }: CellRendererProps<any>) => {
      const { t } = useTranslation()
      return item.isPublished ? (
        <Text>{t('doclets:list:published')}</Text>
      ) : (
        <Text style={{ fontStyle: 'italic', color: theme.colors.disabled }}>
          {t('doclets:list:draft')}
        </Text>
      )
    }
  },
  {
    // Who can open the share link. A draft has no live link, so it shows none
    // even though its gating is already set.
    header: 'Gating',
    width: 130,
    Cell: ({ item }: CellRendererProps<any>) => {
      const { t } = useTranslation()
      if (!item.isPublished) {
        return <Text style={{ color: theme.colors.disabled }}>—</Text>
      }
      return (
        <NoWrapText>
          {t(`doclets:list:gating:${item.gating?.modality ?? 'direct'}`)}
        </NoWrapText>
      )
    }
  },
  {
    header: 'Preview',
    // Room for the bold header text inside the cell's 8px side padding
    width: 104,
    Cell: ({ item }: CellRendererProps<any>) => {
      const { t } = useTranslation()
      return (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('doclets:list:preview')}
          onPress={() => openDocletPreview(item.id)}
          style={{ alignSelf: 'flex-start', padding: 4 }}
        >
          <Feather name="eye" size={18} color={theme.colors.brightPrimary} />
        </Pressable>
      )
    }
  },
  {
    header: 'Delete',
    width: 96,
    Cell: ({ item }: CellRendererProps<any>) => {
      const { t } = useTranslation()
      const { onDelete } = useContext(DocletRowActionsContext)
      return (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('doclets:list:delete', {
            name: item.displayName || t('doclets:untitled')
          })}
          onPress={() =>
            onDelete({
              id: item.id,
              displayName: item.displayName,
              isPublished: item.isPublished
            })
          }
          style={{ alignSelf: 'flex-start', padding: 4 }}
        >
          <Feather
            name="trash-2"
            size={18}
            color={theme.colors.brightPrimary}
          />
        </Pressable>
      )
    }
  }
]
